/**
 * App-wide user-facing copy, routes, and numeric defaults.
 * Import feature blocks as needed: ROUTES, BUYER_LEDGER, CLIENT_CATALOG, PRODUCT, etc.
 */

/** Dashboard / CRM routes */
export const ROUTES = {
  CRM_ADD_CLIENT: '/dashboard/crm-master/add-client',
};

/** Buyer Ledger Creation & edit */
export const BUYER_LEDGER = {
  PAGE_TITLE_CREATE: 'Buyer Ledger Creation',
  PAGE_TITLE_EDIT: 'Edit Buyer Ledger',
  SUBTITLE_CREATE: 'Create and manage buyer ledger information',
  SUBTITLE_EDIT: 'Modify buyer ledger information',
  ALERT_LOAD_FAILED: 'Failed to load client details.',
  ALERT_SAVE_VALIDATION: 'Please fix the errors highlighted below.',
  ALERT_SAVE_FAILED: 'Failed to save. Something went wrong.',
  SUCCESS_UPDATED: (code) => `Client updated (${code})`,
  SUCCESS_CREATED: (code) => `Client saved (Client ID: ${code})`,
  BTN_SAVE: 'Save Ledger',
  BTN_SAVING: 'Saving...',
  LOADING: 'Loading...',
  ALERT_SUCCESS_PREFIX: '✓ Success!',
  ALERT_ERROR_PREFIX: '⚠ Error',
};

/** Client Catalogue (per-buyer product lines) */
export const CLIENT_CATALOG = {
  PAGE_TITLE: 'Client Catalogue',
  PAGE_SUBTITLE: 'Manage product catalogs for each client',
  SELECT_CLIENT_PLACEHOLDER: 'Select Client',
  NO_CLIENTS_YET: 'No clients added yet',
  BTN_ADD_CLIENT: 'Add Client',
  PRODUCTS_IN_CATALOG: (n) =>
    `${n} product${n !== 1 ? 's' : ''} in catalog`,
  BTN_IMPORT_PRODUCTS: 'Import Products',
  EMPTY_SELECT_TITLE: 'Select a Client',
  EMPTY_SELECT_BODY: 'Choose a client from the dropdown to view and manage their catalog',
  EMPTY_CATALOG_TITLE: 'No Products in Catalog',
  EMPTY_CATALOG_BODY: 'Click "Import Products" to add items to this client\'s catalog',
  ITEM_NO_LABEL: 'Item No:',
  NA: 'N/A',
  COLOR_BADGE: (n) => `${n} color${n !== 1 ? 's' : ''}`,
  TITLE_TEARSHEET: 'Open tear sheet',
  TITLE_BOM: 'BOM',
  TITLE_PRODUCT_CYCLE: 'Product Cycle',
  TITLE_REMOVE: 'Remove from catalog',
  ALERT_SELECT_CLIENT_FIRST: 'Please select a client first',
  ALERT_SELECT_PRODUCTS: 'Please select at least one product to import',
  ALERT_CLIENT_CODE_MISSING: 'Client code not found',
  ALERT_IMPORT_SUCCESS: (n) =>
    `Successfully imported ${n} product(s) to client catalog!`,
  ALERT_IMPORT_FAILED: 'Error importing products. Please try again.',
  ALERT_LOAD_CATALOG_FAILED: 'Failed to load client catalog',
  DEV_PHASE: 'In development phase...',
  ALERT_REMOVED: 'Product removed from catalog',
  ALERT_REMOVE_FAILED: 'Failed to remove product from catalog',
  CONFIRM_REMOVE_FROM_CATALOG: 'Are you sure you want to remove this product from the client catalog?',
  IMPORT_MODAL_TITLE: 'Import Products to Client Catalogue',
  FILTERS_TITLE: 'Filters',
  SEARCH_PLACEHOLDER: 'Search products...',
  FILTER_CATEGORY: 'Category',
  FILTER_PRODUCT_NAME: 'Product Name',
  FILTER_FINISH: 'Finish',
  FILTER_COLOR: 'Color',
  FILTER_SUITABLE_FOR: 'Suitable For',
  FILTER_MATERIAL: 'Material',
  FILTER_COLLECTION: 'Collection',
  FILTER_PACK_TYPE: 'Pack Type',
  CLEAR_ALL_FILTERS: 'Clear All Filters',
  BTN_FILTERS: 'Filters',
  SELECTED_COUNT: (n) => `${n} selected`,
  IMPORT_EMPTY_TITLE: 'No products found',
  IMPORT_EMPTY_HINT: 'Try adjusting your filters or search term',
  BTN_CANCEL: 'Cancel',
  BTN_IMPORT: 'Import',
  FILTER_NO_OPTIONS: 'No options available',
  IMPORT_LIMIT: 1000,
};

/** Tear sheet modal (shared labels; CSS lives in ClientCatalog.css) */
export const TEAR_SHEET = {
  EYEBROW: 'Tear sheet',
  EYEBROW_VIEW_ONLY: 'Tear sheet (view only)',
  SECTION_PRODUCT_INFO: 'Product information',
  SECTION_INNER_PACK: 'Inner pack',
  SECTION_MASTER_PACK: 'Master pack',
  SECTION_CONTAINER: 'Container / Loadability',
  SECTION_DOCS: 'Product compliance & approved documents',
  DOCS_HINT:
    'Files are stored in DMS for this buyer and named with your P code. They do not change the global product master.',
  BTN_CLOSE: 'Close',
  BTN_CANCEL: 'Cancel',
  BTN_SAVE: 'Save tear sheet',
  SAVING: 'Saving…',
  SAVE_FAILED: 'Failed to save tear sheet',
  LOADABILITY_EMPTY_HINT:
    'No loadability options configured. In General Master → UOM Loadability, mark one unit as Default and add other units with Capacity (CBM).',
};

/** Product Master (create / edit) */
export const PRODUCT = {
  ROUTE_CATALOG: '/dashboard/product/catalog',
  PAGE_TITLE_CREATE: 'Product Master',
  PAGE_TITLE_EDIT: 'Edit Product',
  SUBTITLE_CREATE: 'Create and manage product information',
  SUBTITLE_EDIT: (uid) => `Editing: ${uid}`,
  LOADING: 'Loading...',
  ALERT_FIX_ERRORS: 'Please fix the errors highlighted below.',
  ALERT_WAIT_IMAGES: 'Please wait for image processing to finish.',
  ALERT_MAX_IMAGES: 'Maximum 5 images allowed',
  ALERT_IMAGE_PROCESS_FAILED: 'Failed to process images. Please try again.',
  ALERT_DELETE_NEW: 'Cannot delete a new product',
  ALERT_PRODUCT_NOT_FOUND: 'Product not found',
  ALERT_DELETE_FAILED: 'Error deleting product. Please try again.',
  ALERT_LOAD_FAILED: 'Error loading product data',
  ALERT_NOT_FOUND_UID: (uid) => `Product not found for UID: ${uid}`,
  DELETE_CONFIRM: (itemNo, uid) =>
    `Are you sure you want to delete product "${itemNo}" (UID: ${uid})? This action cannot be undone.`,
  SUCCESS_DELETED: (itemNo) => `Product "${itemNo}" deleted successfully!`,
  SUCCESS_CREATED: (uid) => `Product created successfully (UID: ${uid})`,
  SUCCESS_UPDATED: (uid) => `Product updated successfully (UID: ${uid})`,
  /** Keys must match submaster API routes */
  SUBMASTER_TYPES: [
    { key: 'category', label: 'Category' },
    { key: 'product-name', label: 'Product Name' },
    { key: 'collection', label: 'Collection' },
    { key: 'materials', label: 'Materials' },
    { key: 'hs-code', label: 'HS Code' },
    { key: 'color', label: 'Color' },
    { key: 'finish', label: 'Finish' },
    { key: 'suitable-for', label: 'Suitable For' },
    { key: 'uom-count', label: 'UOM - Count' },
  ],
};

/** Shared UI */
export const COMMON = {
  LOADING_MORE: 'Loading more products...',
};
